/**
 * The runtime-neutral facade (spec 68 §4.2).
 *
 * `log` / `trace` / `meter` / `track` / `identify` all route through here:
 * apply the per-minute cap (§10) → scrub attributes (§8.1) → (for track)
 * validate against the zod taxonomy (§6.6) → forward to the mounted sink.
 * The mounted sink is noop until a boot function swaps in a real adapter,
 * so every call is safe with zero config.
 */

import { getSchemaVer, validateProps } from '../amplitude/validate.js';
import { type CapSet, createCapSet } from '../caps.js';
import { scrubAttributes } from '../log/redact.js';
import { detectRuntime } from '../runtime.js';
import { getSink } from './registry.js';
import type {
  Attributes,
  Counter,
  Gauge,
  Histogram,
  Logger,
  LogLevel,
  Meter,
  ProductEventContext,
  Span,
  SpanKind,
  Tracer,
  UserTraits,
} from './types.js';

const inertSpan: Span = {
  setAttribute() {
    return this;
  },
  setAttributes() {
    return this;
  },
  recordException() {
    return this;
  },
  setError() {
    return this;
  },
  end() {
    /* dropped-by-cap span */
  },
};

// ── caps ─────────────────────────────────────────────────────────────
let capSet: CapSet = createCapSet(detectRuntime());
let lastDropSummary = 0;

/** Reset caps (test-only; also used when a boot fn re-detects runtime). */
export function resetCaps(now: () => number = Date.now): void {
  capSet = createCapSet(detectRuntime(), now);
  lastDropSummary = 0;
}

/**
 * Emit at most one drop-summary warning per minute (§10.1). Written
 * straight to the sink so it bypasses the log cap (and can't recurse).
 */
function maybeEmitDropSummary(): void {
  const now = Date.now();
  if (now - lastDropSummary < 60_000) return;
  const dropped = capSet.drainDropped();
  const total = dropped.span + dropped.log + dropped.event;
  if (total === 0) return;
  lastDropSummary = now;
  getSink().emitLog({
    level: 'warn',
    message: 'telemetry.caps_exceeded',
    attributes: { dropped_spans: dropped.span, dropped_logs: dropped.log, dropped_events: dropped.event },
  });
}

// ── logging ──────────────────────────────────────────────────────────
function emit(level: LogLevel, message: string, attributes?: Attributes, error?: Error): void {
  if (!capSet.allow('log')) {
    maybeEmitDropSummary();
    return;
  }
  const { attributes: scrubbed, depthExceeded } = scrubAttributes(attributes);
  getSink().emitLog({ level, message, attributes: scrubbed, error });
  if (depthExceeded) {
    getSink().emitLog({ level: 'warn', message: 'redaction_depth_exceeded', attributes: { source: message } });
  }
}

export const log: Logger = {
  debug: (message, attributes) => emit('debug', message, attributes),
  info: (message, attributes) => emit('info', message, attributes),
  warn: (message, attributes) => emit('warn', message, attributes),
  error: (message, attributes, error) => emit('error', message, attributes, error),
  fatal: (message, attributes, error) => emit('fatal', message, attributes, error),
};

// ── tracing ──────────────────────────────────────────────────────────
function beginSpan(name: string, attributes: Attributes | undefined, kind: SpanKind): Span {
  if (!capSet.allow('span')) {
    maybeEmitDropSummary();
    return inertSpan;
  }
  const { attributes: scrubbed } = scrubAttributes(attributes);
  return getSink().startSpan(name, scrubbed, kind);
}

function isThenable(v: unknown): v is Promise<unknown> {
  return typeof v === 'object' && v !== null && typeof (v as { then?: unknown }).then === 'function';
}

export const trace: Tracer = {
  span(name, fn, attributes) {
    const span = beginSpan(name, attributes, 'internal');
    try {
      const result = fn(span);
      if (isThenable(result)) {
        return result.then(
          (value) => {
            span.end();
            return value;
          },
          (err: unknown) => {
            if (err instanceof Error) span.setError(err);
            span.end();
            throw err;
          },
        ) as ReturnType<typeof fn>;
      }
      span.end();
      return result;
    } catch (err) {
      if (err instanceof Error) span.setError(err);
      span.end();
      throw err;
    }
  },
  startSpan(name, attributes, kind = 'internal') {
    return beginSpan(name, attributes, kind);
  },
  currentContext() {
    return getSink().currentContext();
  },
};

// ── metrics (never capped — §9.1) ────────────────────────────────────
export const meter: Meter = {
  counter(name): Counter {
    return {
      add: (value, attributes) => getSink().addToCounter(name, value, scrubAttributes(attributes).attributes),
    };
  },
  histogram(name): Histogram {
    return {
      record: (value, attributes) => getSink().recordHistogram(name, value, scrubAttributes(attributes).attributes),
    };
  },
  gauge(name): Gauge {
    return {
      set: (value, attributes) => getSink().setGauge(name, value, scrubAttributes(attributes).attributes),
    };
  },
};

// ── product events ───────────────────────────────────────────────────
/**
 * Internal (untyped) track. index.ts wraps this in the type-safe
 * `track<E extends ProductEvent>` signature. Zod-validated at runtime:
 * a schema miss drops the event + logs `amplitude.schema_miss`.
 */
export function trackInternal(event: string, props: Attributes, context?: ProductEventContext): void {
  if (!capSet.allow('event')) {
    maybeEmitDropSummary();
    return;
  }
  const result = validateProps(event, props);
  if (!result.ok) {
    log.warn('amplitude.schema_miss', { event, issues: result.issues });
    return;
  }
  const { attributes: scrubbed } = scrubAttributes(result.data);
  getSink().emitEvent({ event, props: scrubbed, context, schemaVer: getSchemaVer(event) });
}

// ── identity ─────────────────────────────────────────────────────────
export function identify(userId: string, traits?: UserTraits): void {
  const scrubbed = scrubAttributes(traits).attributes as UserTraits;
  getSink().identify(userId, traits ? scrubbed : undefined);
}
